import { lazy, useState } from 'react';
import { EditableText } from '@smartfire/components';
import {
    BlockWrapper,
    defineBlock,
    smartProps,
    ChildrenBlock,
    useEditor,
    useInspectorControl,
} from '@smartfire/js-block-engine';
import { EffectCoverflow, Pagination } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/effect-coverflow';
import { Swiper, SwiperSlide } from 'swiper/react';
import style from './block.module.scss';
import FeaturedPostTile from './featured-post-tile';
import { motion } from 'framer-motion';
import { useMediaQuery } from 'react-responsive';

const Inspector = lazy(() => import('./inspector.js'));

const FeaturedPosts = ({ posts, anchorId }) => {
    useInspectorControl(Inspector);
    const { isEditing } = useEditor();
    const isMobile = useMediaQuery({ query: `(max-width: ${style.mobileBreakpoint})` });
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

    const swiperSettings = {
        modules: [Pagination, EffectCoverflow],
        slidesPerView: 'auto',
        spaceBetween: 40,
        loop: false,
        effect: 'coverflow',
        centeredSlides: true,
        coverflowEffect: {
            scale: 0.98,
            rotate: 0,
            slideShadows: false,
        },
        pagination: {
            type: 'bullets',
            clickable: true,
        },
    };

    const slideChangeHandle = (swiper) => {
        setCurrentSlideIndex(swiper.activeIndex);
    };

    return (
        <BlockWrapper id={'anchor-' + anchorId} className={style.featuredPosts}>
            <div className={style.featuredPostsContent}>
                <span className={style.decoration} />
                <EditableText
                    smartProp={'title'}
                    tagName={'p'}
                    placeholder={'Insérez le titre'}
                    className={style.title}
                />
                <EditableText
                    tagName={'p'}
                    smartProp={'subtitle'}
                    placeholder={'Insérez le sous-titre'}
                    allowedFormats={['core/bold', 'core/italic', 'core/link', 'core/text-color']}
                    className={style.subTitle}
                />
                {!isEditing ? (
                    <div className={style.postsList}>
                        {posts && posts.length > 0 && (
                            <>
                                {!isMobile ? (
                                    <>
                                        {posts.map((post, key) => (
                                            <FeaturedPostTile
                                                key={key}
                                                link={post.link}
                                                thumbnail={post.thumbnail}
                                                title={post.title}
                                                isFirst={key === 0}
                                                isMiddle={key === 1}
                                                isLast={key === 2}
                                            />
                                        ))}
                                    </>
                                ) : (
                                    <Swiper
                                        {...swiperSettings}
                                        className={style.slider}
                                        onSlideChange={slideChangeHandle}
                                    >
                                        {posts.map((post, key) => (
                                            <SwiperSlide key={key}>
                                                <FeaturedPostTile
                                                    link={post.link}
                                                    thumbnail={post.thumbnail}
                                                    title={post.title}
                                                    isFirst={key === 0}
                                                    isMiddle={key === 1}
                                                    isLast={key === 2}
                                                    isVisible={currentSlideIndex === key}
                                                    isMobile={isMobile}
                                                />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    <p className={style.postEditing}>Les 3 derniers articles apparaîtront ici</p>
                )}

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ opacity: { duration: 0.2 } }}
                >
                    <ChildrenBlock
                        gutenbergEditorConfig={{
                            orientation: 'horizontal',
                            allowedBlocks: ['smartfire/button'],
                            template: [['smartfire/button']],
                            templateLock: 'all',
                        }}
                        className={style.buttonSeeMore}
                    />
                </motion.div>
            </div>
        </BlockWrapper>
    );
};

export default defineBlock({
    Block: FeaturedPosts,
    smartProps: {
        title: smartProps.string.withDefault('Actualités'),
        subtitle: smartProps.string.withDefault('À la une'),
        anchorId: smartProps.string.withDefault('a-la-une'),
    },
    setup: {
        title: 'Articles à la une',
    },
});
